import { __rest } from "tslib";
import React, { useState, useImperativeHandle, forwardRef, useMemo, useCallback, } from 'react';
import PropTypes from 'prop-types';
import Popover from '../Popover';
import RawText from '../Text/RawText';
import { dataHooks, TIMEOUT } from './constants';
import { st, classes } from './Tooltip.st.css.js';
const Tooltip = forwardRef((_a, ref) => {
    var { onShow, onHide, size, className, placement, exitDelay, enterDelay, moveArrowTo, appendTo, flip, fixed, maxWidth, fluid, moveBy, disabled, children, zIndex, textAlign, content, inline, 'aria-describedby': ariaDescribedBy, 'aria-labelledby': ariaLabelledBy } = _a, rest = __rest(_a, ["onShow", "onHide", "size", "className", "placement", "exitDelay", "enterDelay", "moveArrowTo", "appendTo", "flip", "fixed", "maxWidth", "fluid", "moveBy", "disabled", "children", "zIndex", "textAlign", "content", "inline", 'aria-describedby', 'aria-labelledby']);
    const [shown, setShown] = useState(false);
    useImperativeHandle(ref, () => ({ open, close }));
    const open = useCallback(() => {
        setShown(true);
        onShow === null || onShow === void 0 ? void 0 : onShow();
    }, [onShow]);
    const close = useCallback(() => {
        setShown(false);
        onHide === null || onHide === void 0 ? void 0 : onHide();
    }, [onHide]);
    const isDisabled = useMemo(() => {
        var _a;
        return disabled === undefined
            ? // @ts-expect-error
                (children === null || children === void 0 ? void 0 : children.props) && ((_a = children === null || children === void 0 ? void 0 : children.props) === null || _a === void 0 ? void 0 : _a.disabled)
            : disabled;
    }, [children, disabled]);
    const onFocus = useCallback((event, handlers) => {
        const focusableHOC = handlers && handlers.focus;
        const target = event.target;
        try {
            if (process.env.NODE_ENV !== 'test' &&
                target instanceof Element &&
                !target.matches(':focus-visible')) {
                handlers === null || handlers === void 0 ? void 0 : handlers.blur();
                return;
            }
        }
        catch (e) {
            // browser does not support `matches(':focus-visible')` fallback to original focus behavior
        }
        open();
        return focusableHOC ? handlers.focus() : null;
    }, [open]);
    const onBlur = useCallback((_event, handlers) => {
        const focusableHOC = handlers && handlers.blur;
        close();
        return focusableHOC ? handlers.blur() : null;
    }, [close]);
    const triggerElement = useMemo(() => {
        if (typeof children === 'string' || !children) {
            return children;
        }
        if (React.isValidElement(children)) {
            return React.cloneElement(children, Object.assign({ onFocus: isDisabled ? undefined : onFocus, onBlur: isDisabled ? undefined : onBlur }, (ariaLabelledBy
                ? { 'aria-labelledby': ariaLabelledBy }
                : { 'aria-describedby': ariaDescribedBy })));
        }
        if (typeof children === 'function') {
            return children({
                onFocus,
                onBlur,
            });
        }
        return;
    }, [
        children,
        isDisabled,
        onFocus,
        onBlur,
        ariaDescribedBy,
        ariaLabelledBy,
    ]);
    const contentElement = useMemo(() => {
        const textSize = size === 'small' ? 'tiny' : 'small';
        const textWeight = size === 'small' ? 'normal' : 'thin';
        return (React.createElement("div", { style: { textAlign } }, typeof content === 'string' ? (
        // @ts-ignore
        React.createElement(RawText, { dataHook: dataHooks.tooltipText, size: textSize, weight: textWeight, light: true }, content)) : (content)));
    }, [content, textAlign, size]);
    return (React.createElement(Popover, Object.assign({ role: "tooltip", showArrow: size !== 'small', className: st(classes.root, { size, inline }, className), placement: placement, shown: isDisabled ? false : shown, onMouseEnter: isDisabled ? undefined : open, onMouseLeave: close, onClickOutside: close, "data-size": size, hideDelay: exitDelay, showDelay: enterDelay, moveBy: moveBy, moveArrowTo: moveArrowTo, appendTo: appendTo, id: ariaLabelledBy || ariaDescribedBy, flip: flip, fixed: fixed, maxWidth: maxWidth, fluid: fluid, disableClickOutsideWhenClosed: true, zIndex: zIndex }, rest, { theme: "dark", timeout: TIMEOUT }),
        React.createElement(Popover.Element, null, triggerElement),
        React.createElement(Popover.Content, null, contentElement)));
});
Tooltip.defaultProps = {
    placement: 'top',
    appendTo: 'window',
    onShow: () => ({}),
    onHide: () => ({}),
    enterDelay: 0,
    exitDelay: 0,
    maxWidth: 204,
    flip: true,
    fixed: false,
    textAlign: 'start',
    size: 'medium',
    zIndex: 6000,
    content: '',
};
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
    dataHook: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    content: PropTypes.node,
    textAlign: PropTypes.oneOf(['start', 'center']),
    enterDelay: PropTypes.number,
    exitDelay: PropTypes.number,
    // @ts-expect-error
    moveBy: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
    moveArrowTo: PropTypes.number,
    // @ts-expect-error
    appendTo: PropTypes.oneOfType([
        PropTypes.oneOf(['window', 'scrollParent', 'viewport', 'parent']),
        PropTypes.element,
        PropTypes.func,
    ]),
    flip: PropTypes.bool,
    fixed: PropTypes.bool,
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onShow: PropTypes.func,
    onHide: PropTypes.func,
    placement: PropTypes.oneOf([
        'auto-start',
        'auto',
        'auto-end',
        'top-start',
        'top',
        'top-end',
        'right-start',
        'right',
        'right-end',
        'bottom-end',
        'bottom',
        'bottom-start',
        'left-end',
        'left',
        'left-start',
    ]),
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium']),
    'aria-describedby': PropTypes.string,
    'aria-labelledby': PropTypes.string,
    zIndex: PropTypes.number,
    fluid: PropTypes.bool,
};
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map